"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScrollHint = void 0;
const lit_1 = require("lit");
const decorators_js_1 = require("lit/decorators.js");
require("@/components/common/icon-button/semantics/prev-button");
require("@/components/common/icon-button/semantics/next-button");
const resize_controller_1 = require("@/controllers/resize-controller");
const schedule_controller_1 = require("@/controllers/schedule-controller");
/**
 * 가로 스크롤 영역의 한쪽 끝. 그쪽에 가려진 콘텐츠가 있는 동안만 흐림 레이어와
 * 그 방향으로 넘기는 버튼을 보인다. 트랙패드·가로 휠이 없어도 넘길 수 있게 하는 것이 버튼의 몫이다.
 * mm-scroll(direction=row)이 양 끝에 렌더한다.
 */
let ScrollHint = class ScrollHint extends lit_1.LitElement {
    constructor() {
        super(...arguments);
        this.placement = 'end';
        this.size = 'medium';
        this.measureFrame = new schedule_controller_1.ScheduleController(this, () => this.handleScrollRootResize());
        this.resize = new resize_controller_1.ResizeController(this, {
            getTargets: () => this.resizeTargets,
            onResize: () => this.handleScrollRootResize(),
        });
        this.handlePrevClick = () => this.scrollByPage(-1);
        this.handleNextClick = () => this.scrollByPage(1);
        // gap은 측정값이고 host가 소비하므로 host에 직접 주입한다.
        // 스크롤 중에는 스타일을 건드리지 않아야 매 프레임 레이아웃을 다시 계산하지 않는다. gap은 크기가 바뀔 때만 잰다.
        this.handleScrollRootResize = () => {
            const root = this.scrollRoot;
            if (!root)
                return;
            const gap = getComputedStyle(root).columnGap;
            this.style.setProperty('--scroll-hint-gap', gap === 'normal' ? '0px' : gap);
            this.handleScrollRootScroll();
        };
        this.handleScrollRootScroll = () => {
            const root = this.scrollRoot;
            if (!root)
                return;
            const maxScrollLeft = root.scrollWidth - root.clientWidth;
            const scrollLeft = Math.abs(root.scrollLeft);
            const canScroll = maxScrollLeft > 1;
            const isAtStart = scrollLeft <= 1;
            const isAtEnd = scrollLeft >= maxScrollLeft - 1;
            const hidden = !canScroll || (this.placement === 'start' ? isAtStart : isAtEnd);
            if (this.hidden !== hidden)
                this.hidden = hidden;
        };
    }
    connectedCallback() {
        super.connectedCallback();
        // 첫 측정 전에는 숨겨 둬야, 넘치지 않는 영역에서 흐림이 보였다가 사라지는 깜빡임이 없다.
        this.hidden = true;
        // shadow root 최상위에 렌더되면 parentElement가 없으므로 그 host가 스크롤 영역이다.
        const parent = this.parentNode;
        this.scrollRoot =
            parent instanceof ShadowRoot ? parent.host : this.parentElement ?? undefined;
        this.scrollRoot?.addEventListener('scroll', this.handleScrollRootScroll);
        // scrollRoot는 연결된 뒤에야 알 수 있어, 연결 시점의 관찰 대상을 여기서 다시 맞춘다.
        this.resize.refresh();
        this.measureFrame.request();
    }
    disconnectedCallback() {
        this.scrollRoot?.removeEventListener('scroll', this.handleScrollRootScroll);
        this.scrollRoot = undefined;
        super.disconnectedCallback();
    }
    render() {
        if (this.placement === 'start') {
            return (0, lit_1.html) `
        <mm-prev-button size=${this.size} @prev=${this.handlePrevClick}></mm-prev-button>
      `;
        }
        return (0, lit_1.html) `
      <mm-next-button size=${this.size} @next=${this.handleNextClick}></mm-next-button>
    `;
    }
    // scrollRoot 자체는 크기가 고정돼 있어도 슬라이드 이미지가 늦게 로드되며 scrollWidth만 늘어날 수 있으므로,
    // 콘텐츠 자식들도 함께 관찰해야 그 변화를 잡아낸다. 합성 컴포넌트가 slot으로 넘긴 자식은 slot을 펼쳐 관찰한다.
    get resizeTargets() {
        if (!this.scrollRoot)
            return [this];
        const children = Array.from(this.scrollRoot.children).flatMap(child => child instanceof HTMLSlotElement ? child.assignedElements({ flatten: true }) : [child]);
        return [this, this.scrollRoot, ...children];
    }
    // 한 번에 보이는 폭의 80%씩 넘겨, 직전 화면의 끝 항목이 다음 화면에서도 보이게 한다.
    scrollByPage(direction) {
        const root = this.scrollRoot;
        if (!root)
            return;
        root.scrollBy({ left: direction * root.clientWidth * 0.8, behavior: 'smooth' });
    }
};
ScrollHint.styles = (0, lit_1.css) `
    :host {
      --scroll-hint-width: var(--size-80);
      --scroll-hint-gap: 0px;
      --_gradient-direction: to right;

      display: flex;
      flex: 0 0 var(--scroll-hint-width);
      order: 999;
      align-self: stretch;
      align-items: center;
      justify-content: flex-end;
      min-height: var(--size-32);
      margin-left: calc((var(--scroll-hint-width) + var(--scroll-hint-gap)) * -1);
      position: sticky;
      right: -1px;
      z-index: var(--material-zindex-elevated);
      pointer-events: none;
      transition: opacity var(--transition-duration) var(--transition-easing),
        visibility var(--transition-duration) var(--transition-easing);
      background: linear-gradient(
        var(--_gradient-direction),
        transparent 0%,
        color-mix(in srgb, var(--background-color) 50%, transparent) 55%,
        color-mix(in srgb, var(--background-color) 82%, transparent) 80%,
        var(--background-color) 100%
      );
    }

    :host([placement='start']) {
      --_gradient-direction: to left;

      order: -999;
      justify-content: flex-start;
      margin-right: calc((var(--scroll-hint-width) + var(--scroll-hint-gap)) * -1);
      margin-left: 0;
      left: 0;
      right: auto;
    }

    :host([hidden]) {
      opacity: 0;
      visibility: hidden;
    }

    mm-prev-button,
    mm-next-button {
      pointer-events: auto;
    }
  `;
__decorate([
    (0, decorators_js_1.property)({ type: String, reflect: true })
], ScrollHint.prototype, "placement", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String })
], ScrollHint.prototype, "size", void 0);
ScrollHint = __decorate([
    (0, decorators_js_1.customElement)('mm-scroll-hint')
], ScrollHint);
exports.ScrollHint = ScrollHint;
//# sourceMappingURL=data:application/json;base64,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